import Spacer from '@berlitz/spacer'
import ArrowLeft from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowLeft1'
import ArrowRight from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowRight1'
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import { MMIButton } from './ManageInstructorView/style'
import { CarouselWrapper, ControlBtn, Controls, ControlWrapper, ScrollView } from './style'
import { useFave } from './context'
import { FMIStudentProfileContext } from './context'

interface CarouselProps {
  items?: React.ReactNode[]
  controls?: boolean
  loading?: boolean
  hideEdit?: boolean
  isEditMode?: boolean
  onEditClick?: () => void
}

function useWindowSize() {
  const [size, setSize] = useState([0, 0])
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight])
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [])
  return size
}

const Carousel: React.FC<CarouselProps> = ({ items: itemsProp = [], loading, hideEdit, onEditClick, isEditMode }) => {
  const fave = useFave()
  const { isAdmin } = useContext(FMIStudentProfileContext)
  const [width, height] = useWindowSize()
  const [items, setItems] = useState<React.ReactNode[]>(itemsProp)
  const [startEnd, setStartEnd] = useState<IntersectionObserverEntry[]>()
  const [hasReachedStart, setHasReachedStart] = useState(true)
  const [hasReachedEnd, setHasReachedEnd] = useState(true)
  const [isScrollable, setIsScrollable] = useState(false)
  const elemWrapper = useRef<HTMLDivElement | null>(null)
  const elem = useRef<HTMLDivElement | null>(null)
  const uniqueId = Date.now() + '' + Math.floor(Math.random() * 1000)
  const buttonLabel = isEditMode ? 'Done' : 'Edit'

  const setHasReached = () => {
    if (elem.current) {
      setHasReachedStart(elem.current.scrollLeft <= 50)
      setHasReachedEnd(elem.current.scrollLeft + elem.current.offsetWidth >= elem.current.scrollWidth)
    }
  }

  const scroll = (dir: 'right' | 'left') => {
    if (!elem.current) {
      return
    }

    if (dir === 'right') {
      elem.current.scrollLeft += 200
    } else {
      elem.current.scrollLeft -= 200
    }

    setHasReached()
  }

  useEffect(() => {
    setItems(itemsProp)
  }, [itemsProp])

  const startEndListener = (entries) => {
    setStartEnd(entries)
  }

  useEffect(() => {
    const currentScroll = elem.current?.scrollLeft || 0
    const entry = startEnd?.[startEnd?.length - 1]
    if (!entry) {
      return
    }

    const isStart = entry?.target?.id === `scroll-start-${uniqueId}`
    const isEnd = entry?.target?.id === `scroll-end-${uniqueId}`

    const noOverflow = elem.current?.offsetWidth === elem.current?.scrollWidth

    setHasReachedStart((isStart && entry.isIntersecting) || currentScroll === 0 || noOverflow)
    setHasReachedEnd((isEnd && entry.isIntersecting) || noOverflow)
  }, [startEnd])

  useLayoutEffect(() => {
    if (loading) {
      return
    }

    const options = {
      root: elem.current,
      rootMargin: '0px',
      threshold: 1,
    }

    const startEndObserver = new IntersectionObserver(startEndListener, options)

    const obsStartEnd = [
      document.getElementById(`scroll-start-${uniqueId}`), // first tile
      document.getElementById(`scroll-end-${uniqueId}`), // last tile
    ].filter(Boolean)

    obsStartEnd.map((el) => startEndObserver.observe(el as HTMLDivElement))
    setItems(itemsProp)
    document.getElementById('carousel-scoll-view')?.addEventListener('scroll', () => setHasReached())

    return () => {
      startEndObserver.disconnect()
      document.getElementById('carousel-scoll-view')?.removeEventListener('scroll', () => {})
    }
  }, [loading, itemsProp])

  useLayoutEffect(() => {
    if (elem.current) {
      elemWrapper.current && setIsScrollable(elem.current?.scrollWidth > elemWrapper.current?.offsetWidth - 75)
      setHasReached()
    }
  }, [elem.current?.scrollWidth, elemWrapper.current?.offsetWidth, width, height])

  return (
    <CarouselWrapper ref={elemWrapper || null}>
      <ScrollView id="carousel-scoll-view" ref={elem || null}>
        {items?.map((item, i) => (
          <div
            key={i}
            id={i === 0 ? `scroll-start-${uniqueId}` : i === items.length - 1 ? `scroll-end-${uniqueId}` : ''}
          >
            {item}
          </div>
        ))}
      </ScrollView>
      <Controls>
        <ControlWrapper>
          {loading && (
            <>
              <Skeleton width="32px" height="34px" />
              <Skeleton width="32px" height="34px" />
            </>
          )}
          {isScrollable && (
            <>
              <ControlBtn disabled={hasReachedStart} onClick={() => scroll('left')}>
                <ArrowLeft />
              </ControlBtn>
              <ControlBtn disabled={hasReachedEnd} onClick={() => scroll('right')}>
                <ArrowRight />
              </ControlBtn>
            </>
          )}
        </ControlWrapper>

        {!loading && !isAdmin && !hideEdit ? (
          <div>
            <Spacer size="xs" />
            <MMIButton outline onClick={() => (onEditClick ? onEditClick() : fave.setIsEditMode(!fave.isEditMode))}>
              <FormattedMessage id={buttonLabel} defaultMessage={buttonLabel} />
            </MMIButton>
          </div>
        ) : null}
      </Controls>
    </CarouselWrapper>
  )
}

export default Carousel
